import { ApolloError } from "@apollo/client";
import {
  useDeleteImmigrationDocumentMutation,
  useExhibitFilesQuery,
  useImmigrationDocsByUserQuery,
  useUnlinkExhibitFileMutation,
} from "@codegen/index";
import { ImmigrationDocumentType } from "@codegen/schema";
import { FormContainer, OnboardingContainer } from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { UploadFileStatus } from "@components/uploader/types";
import Uploader from "@components/uploader/uploader";
import { OnboardingPageType } from "@pages/onboarding/types";
import * as Sentry from "@sentry/react";
import { api } from "@utils/api";
import { useLogError } from "@utils/error";
import { useJwt } from "@utils/hooks";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { z } from "zod";
export const passportFormSchema = z.object({
  passports: z.string().min(1, "Please upload your passport"),
});

export type PassportFormType = z.infer<typeof passportFormSchema>;
export const PassportForm: React.FC<OnboardingPageType> = (props) => {
  const {
    contact,
    onBack,
    onSubmit,
    autoSave,
    onSkip,
    exhibit,
    onSkipForNow,
    moduleProgress,
    currentStep,
    showSkipForNow,
    hideExitButton,
  } = props;

  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const { isReadOnly } = useReadOnlyMode();
  const token = useJwt();
  const logError = useLogError();
  const fileType = ImmigrationDocumentType.Passport;

  // Query immigration documents
  const { data, loading, refetch } = useImmigrationDocsByUserQuery({
    variables: {
      ownerId: contact.id,
      type: fileType,
    },
  });

  const serverFiles = useMemo(() => {
    return (
      data?.allImmigrationDocuments?.nodes
        ?.map((node) => node?.fileByFileId)
        .filter((n) => !!n)
        .map((x) => ({
          id: x.id ?? "",
          name: x.name ?? "",
        })) ?? []
    );
  }, [data]);

  const [currentFiles, setCurrentFiles] = useState(serverFiles);

  useEffect(() => {
    setCurrentFiles(serverFiles);
  }, [serverFiles]);

  // Mutations for file operations
  const [unlinkExhibitFile] = useUnlinkExhibitFileMutation();
  const [deleteImmigrationDocument] = useDeleteImmigrationDocumentMutation();

  // Query exhibit files
  const { data: exhibitFiles } = useExhibitFilesQuery({
    variables: {
      id: exhibit.exhibitId?.toString() ?? "",
    },
  });

  // Handle file upload
  const uploadFile = api.files.uploadImmigrationDoc;

  const handleSave = useCallback(
    async (
      files: Array<{
        id: string;
        name: string;
        success?: boolean;
        fileId?: string;
      }>
    ) => {
      try {
        setStatus("loading");
        if (files?.length && files.length > 0) {
          await refetch();
          const fileIds = files?.map((n) => n?.id);
          autoSave({ files: fileIds });
        } else if (!exhibit.required) {
          onSkip?.();
        }
        setStatus("success");
      } catch (e) {
        setStatus("error");
        logError(e, { message: "Error saving passport files" });
      }
    },
    [refetch, exhibit.required, onSkip, autoSave, logError]
  );

  const handleSubmit = useCallback(async () => {
    try {
      setStatus("loading");
      if (currentFiles?.length && currentFiles.length > 0) {
        await refetch();
        const fileIds = currentFiles?.map((n) => n?.id);
        onSubmit({ files: fileIds });
      } else if (!exhibit.required) {
        onSkip?.();
      }
      setStatus("success");
    } catch (e) {
      setStatus("error");
      logError(e, { message: "Error submitting passport files" });
    }
  }, [currentFiles, refetch, exhibit.required, onSkip, onSubmit, logError]);

  const handleSkipForNow = useCallback(async () => {
    try {
      if (currentFiles?.length && currentFiles.length > 0) {
        await refetch();
        const fileIds = currentFiles?.map((n) => n?.id);
        onSkipForNow?.({ files: fileIds });
      } else {
        onSkipForNow?.();
      }
    } catch (e) {
      logError(e, { message: "Error skipping" });
    }
  }, [currentFiles, refetch, onSkipForNow, logError]);

  // Delete file handler
  const handleDelete = useCallback(
    async (file: UploadFileStatus) => {
      try {
        setStatus("loading");
        const exhibitFile =
          exhibitFiles?.exhibitById?.exhibitFilesByExhibitId?.nodes?.find(
            (f) => f?.fileByFileId?.id === file.id
          );

        // Unlink from exhibit if it exists
        if (exhibit.exhibitId && exhibitFile) {
          Sentry.addBreadcrumb({
            category: "exhibit",
            message: `Unlinking exhibit file ${file.id} from exhibit ${exhibit.exhibitId}`,
          });

          await unlinkExhibitFile({
            variables: {
              input: {
                clientMutationId: `unlink-exhibit-file-${exhibit.exhibitId}-${file.id}`,
                exhibitId: exhibit.exhibitId.toString(),
                fileId: file.id,
              },
            },
          });
        }

        // Delete the file
        if (file.id) {
          try {
            const { errors } = await deleteImmigrationDocument({
              variables: {
                input: {
                  clientMutationId: `delete-user-immigration-doc-${fileType}-${contact.id}-${file.id}`,
                  ownerId: contact.id,
                  fileId: file.id,
                },
              },
            });

            if (errors != null) {
              throw new Error(errors[0].message);
            }
          } catch (e) {
            if (e instanceof Error) {
              logError(e, { contactId: contact.id, fileId: file.id });
            } else if (e instanceof ApolloError) {
              logError(e.graphQLErrors[0], {
                contactId: contact.id,
                fileId: file.id,
              });
            } else {
              logError(e, { contactId: contact.id, fileId: file.id });
            }
            throw e;
          }
        }

        await refetch();
        setCurrentFiles((prev) => prev.filter((f) => f.id !== file.id));
        setStatus("success");
      } catch (e) {
        setStatus("error");
        logError(e, { contactId: contact.id, fileId: file.id });
        toast.error("An error occurred while deleting the file");
      }
    },
    [
      contact.id,
      exhibit.exhibitId,
      exhibitFiles,
      unlinkExhibitFile,
      deleteImmigrationDocument,
      fileType,
      refetch,
      logError,
    ]
  );

  // File upload handler
  const onFileUpload = useCallback(
    async (file: File) => {
      try {
        setStatus("loading");
        const uploadResult = await uploadFile(
          file,
          token,
          contact.id,
          fileType
        );

        if (!uploadResult.success || !uploadResult.id)
          throw new Error("An error occurred while uploading the file");

        const { id } = uploadResult;
        const newFile = {
          id,
          name: file.name,
          success: uploadResult.success,
          fileId: id,
        };

        // Optimistically update currentFiles for immediate UI feedback
        const updatedFiles = [...currentFiles, newFile];
        // setCurrentFiles will be updated by the useEffect listening to serverFiles after refetch in handleSave
        // However, to ensure handleSave gets the absolute latest, we pass updatedFiles.
        // And for immediate UI feedback from the Uploader which might use currentFiles, we update it here.
        setCurrentFiles(updatedFiles);

        // Then call handleSave which will autoSave and then refetch
        await handleSave(updatedFiles);
        setStatus("success");
        return newFile;
      } catch (exception) {
        setStatus("error");
        logError(exception, {
          contactId: contact.id,
          fileType,
        });
        return {
          success: false,
          name: file.name,
          type: file.type,
          link: "",
          id: "",
        };
      }
    },
    [contact.id, token, fileType, refetch, currentFiles, handleSave, logError]
  );

  return (
    <OnboardingContainer
      key={exhibit.id}
      title="Identification Documents"
      subtitle="Upload a copy of your passport. If you have multiple, select only one for identity verification."
      onSubmit={handleSubmit}
      exhibit={exhibit}
      name="passport-form"
      onBack={onBack}
      onSkipForNow={exhibit.required ? handleSkipForNow : onSkip}
      isLoading={status === "loading"}
      currentStep={currentStep}
      progress={moduleProgress}
      showSkipForNow={showSkipForNow}
      hideExitButton={hideExitButton}
    >
      <FormContainer
        status={status}
        errorMessage="There was a problem uploading your passport. Please try again."
        successMessage={
          status === "success" ? "Files uploaded successfully." : undefined
        }
      >
        <div>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-accent rounded-full border-t-transparent"></div>
            </div>
          ) : (
            <Uploader
              key={exhibit.id}
              multiple
              onFileUpload={onFileUpload}
              defaultFiles={currentFiles}
              disabled={isReadOnly}
              allowDelete
              onDeleteFile={handleDelete}
            />
          )}
        </div>
      </FormContainer>
    </OnboardingContainer>
  );
};

export default PassportForm;
